import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../utils/navigation';
import MobileSearchOverlay from './MobileSearchOverlay';

/**
 * Barre de navigation mobile — 5 onglets RÉELS (nav-first, pas notifications).
 *
 * Layout : Accueil · Biens · [ ⊕ Créer ] · Tâches · Contacts
 *   - Le bouton central « Créer » ouvre une bottom-sheet avec des actions
 *     EXISTANTES uniquement, câblées via `useNavigate()` (composant autonome —
 *     pas de handlers externes obligatoires, ce qui évite les régressions
 *     silencieuses quand un parent oublie de brancher les props).
 *   - Onglet actif dérivé de `useLocation().pathname` (pas de prop externe).
 *   - Notifications (Alertes / Emails) retirées d'ici et remontées sur la
 *     cloche du header (badge global).
 *
 * ⚠️ MOBILE ONLY (< 768 px) — masqué en desktop via `md:hidden`.
 */
const BottomNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // ── Détermination de l'onglet actif à partir de l'URL ─────────────────────
  const path = location.pathname;
  const isActive = useCallback((id) => {
    if (id === 'home') return path === '/' || path === '/dashboard';
    if (id === 'biens') return path.startsWith('/properties') || path.startsWith('/biens');
    // L'onglet Recherche est actif tant que l'overlay est ouvert (pas de route dédiée).
    if (id === 'recherche') return searchOpen;
    if (id === 'contacts') return path.startsWith('/contacts');
    return false;
  }, [path, searchOpen]);

  const go = useCallback((to) => {
    setSheetOpen(false);
    setSearchOpen(false);
    navigate(to);
  }, [navigate]);

  const items = [
    { id: 'home',     icon: 'bi-house',        activeIcon: 'bi-house-fill',        label: 'Accueil',  to: ROUTES.DASHBOARD },
    { id: 'biens',    icon: 'bi-building',     activeIcon: 'bi-building-fill',     label: 'Biens',    to: ROUTES.PROPERTIES },
    // Onglet central — bouton d'action distinct (bottom-sheet « Créer »).
    { id: 'create',   isCenter: true,          label: 'Créer' },
    // Recherche remplace l'onglet Tâches (les tâches restent accessibles via
    // ⊕ Créer → « Nouvelle tâche » et le tableau de bord Accueil).
    // v1.1 : quand le module Agenda arrivera, il reprendra cet emplacement.
    { id: 'recherche', icon: 'bi-search',      activeIcon: 'bi-search',            label: 'Recherche', action: 'openSearch' },
    { id: 'contacts', icon: 'bi-people',        activeIcon: 'bi-people-fill',      label: 'Contacts', to: ROUTES.CONTACTS },
  ];

  // Empêcher le scroll de la page quand la sheet est ouverte
  useEffect(() => {
    if (sheetOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [sheetOpen]);

  // ── Actions de la bottom-sheet ────────────────────────────────────────────
  // Uniquement des actions DÉJÀ EXISTANTES. Chaque handler navigue directement
  // vers la route/paramètre qui déclenche l'action (wizard, formulaire contact,
  // modal « Nouvelle tâche » via ?new=1 sur /taches). Les 2 items « bientôt »
  // sont désactivés (pas de handler) et affichent un tag « bientôt ».
  const createActions = [
    {
      id: 'new-property',
      icon: 'bi-building-add',
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      label: 'Nouveau bien',
      hint: 'Wizard guidé',
      onClick: () => go('/properties/wizard/new'),
      testid: 'sheet-new-property',
    },
    {
      id: 'new-contact',
      icon: 'bi-person-plus',
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      label: 'Contact express',
      hint: 'Saisie rapide',
      onClick: () => go('/contacts/express'),
      testid: 'sheet-new-contact',
    },
    {
      id: 'new-visite',
      icon: 'bi-door-open',
      iconColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
      label: 'Nouvelle visite',
      hint: 'Bon de visite et dénonce',
      onClick: () => go('/visites/nouvelle'),
      testid: 'sheet-new-visite',
    },
    {
      id: 'new-task',
      icon: 'bi-check2-square',
      iconColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      label: 'Nouvelle tâche',
      hint: 'Rappel / à faire',
      // /taches?new=1 → TasksPage détecte ce paramètre et ouvre directement le
      // modal de création (voir TasksPage.jsx, effet sur searchParams).
      onClick: () => go('/taches?new=1'),
      testid: 'sheet-new-task',
    },
    // ── Aperçu roadmap — DÉSACTIVÉS (sans handler) ─────────────────────────
    {
      id: 'soon-voice',
      icon: 'bi-mic',
      iconColor: 'text-slate-400',
      bgColor: 'bg-slate-100',
      label: 'Bien à la voix',
      hint: null,
      disabled: true,
      testid: 'sheet-soon-voice',
    },
    {
      id: 'soon-panneau',
      icon: 'bi-camera',
      iconColor: 'text-slate-400',
      bgColor: 'bg-slate-100',
      label: 'Capturer un panneau',
      hint: null,
      disabled: true,
      testid: 'sheet-soon-panneau',
    },
  ];

  return (
    <>
      <nav
        data-testid="bottom-navigation"
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around items-stretch" style={{ minHeight: '58px' }}>
          {items.map((item) => {
            if (item.isCenter) {
              return (
                <button
                  key="create"
                  type="button"
                  onClick={() => setSheetOpen(true)}
                  data-testid="bottom-nav-create"
                  className="flex flex-col items-center justify-end flex-1 relative"
                  aria-label="Créer"
                >
                  {/* Bouton central proéminent — remonte au-dessus de la barre */}
                  <span
                    className="absolute -top-5 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-600/30 flex items-center justify-center ring-4 ring-white active:scale-95 transition"
                  >
                    <i className="bi bi-plus-lg text-2xl leading-none" />
                  </span>
                  <span className="text-[10px] font-semibold text-blue-600 mb-1.5 mt-8">{item.label}</span>
                </button>
              );
            }

            const active = isActive(item.id);
            // Handler onglet : soit navigation, soit action interne (ex. ouverture overlay recherche).
            const handleTabClick = () => {
              if (item.action === 'openSearch') {
                setSheetOpen(false);
                setSearchOpen(true);
                return;
              }
              go(item.to);
            };
            return (
              <button
                key={item.id}
                type="button"
                data-testid={`bottom-nav-${item.id}`}
                onClick={handleTabClick}
                aria-current={active ? 'page' : undefined}
                className="flex flex-col items-center justify-center flex-1 relative transition-colors"
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-0.5 bg-blue-600 rounded-b-full" />
                )}
                <i className={`${active ? item.activeIcon : item.icon} text-xl ${active ? 'text-blue-600' : 'text-slate-500'}`} />
                <span className={`text-[10px] font-medium mt-0.5 ${active ? 'text-blue-600' : 'text-slate-500'}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Bottom sheet « Créer » ────────────────────────────────────────── */}
      {sheetOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          data-testid="create-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Créer un élément"
        >
          {/* Overlay */}
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setSheetOpen(false)}
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
          />
          {/* Sheet */}
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-slide-up"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>
            <div className="px-5 pt-2 pb-3">
              <h2 className="text-base font-bold text-slate-900">Créer</h2>
              <p className="text-xs text-slate-500 mt-0.5">Choisissez ce que vous souhaitez ajouter</p>
            </div>
            <ul className="px-2 pb-4 space-y-1">
              {createActions.map((a) => {
                const isDisabled = !!a.disabled;
                return (
                  <li key={a.id}>
                    <button
                      type="button"
                      data-testid={a.testid}
                      onClick={isDisabled ? undefined : a.onClick}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition ${
                        isDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:bg-slate-50 active:bg-slate-100'
                      }`}
                    >
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.bgColor}`}>
                        <i className={`bi ${a.icon} text-xl ${a.iconColor}`} />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-2">
                          <span className={`text-[15px] font-semibold ${isDisabled ? 'text-slate-500' : 'text-slate-900'}`}>
                            {a.label}
                          </span>
                          {isDisabled && (
                            <span
                              className="text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-slate-200 rounded-full px-2 py-0.5"
                              data-testid={`${a.testid}-badge-soon`}
                            >
                              bientôt
                            </span>
                          )}
                        </span>
                        {a.hint && (
                          <span className="block text-xs text-slate-500 mt-0.5">{a.hint}</span>
                        )}
                      </span>
                      {!isDisabled && <i className="bi bi-chevron-right text-slate-400" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* ── Overlay Recherche globale plein écran (mobile-only) ─────────── */}
      <MobileSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default BottomNavigation;
